import { WatchWithFilterOptions } from "@vueuse/core";

/**
 * activatedWatch is meant to detect changes in the destination route
 * and remove watching variables when the component is no longer active, but still present/cached
 * because of the keep-alive feature on the router.
 *
 * @param varsToWatch Same as first variable passed to the normal watch command
 * @param fn Same as the 2nd variable passed to the normal watch command
 */
export function activatedWatch(
  varsToWatch: any,
  fn: () => void,
  options?: WatchWithFilterOptions<false>,
) {
  const route = useRoute();
  const router = useRouter();
  const debug = useDebug();
  let activated = true;

  const initialPath = route.path;
  debug.log("activatedWatch: intial path", initialPath);

  //when path is reactivated, go ahead and check for var changes
  // since the last time and rerun fn if necessary
  onActivated(async () => {
    debug.log("activatedWatch: path activated");
    activated = true;
    const activatedValues = JSON.stringify(varsToWatch());
    if (lastActivatedValues !== activatedValues) {
      debug.log("activatedWatch: found changes, rerunning fn", activatedValues);
      await fn();
    }
    resume();
  });

  let lastActivatedValues: string = "";

  //capture values before router changes
  router.beforeEach((from, to, next) => {
    if (activated) {
      lastActivatedValues = JSON.stringify(varsToWatch());
    }
    next();
  });

  //stop watching for changes since path is no longer active
  onDeactivated(() => {
    activated = false;
    pause();
    debug.log("activatedWatch: path deactivated", lastActivatedValues);
  });

  //while path is active, watch for changes in vars and run fn if there are changes
  const { pause, resume } = watchPausable(
    varsToWatch,
    async () => {
      await nextTick();

      if (route.path !== initialPath) {
        return;
      }

      if (!activated) {
        return;
      }

      debug.log("activatedWatch: running watch command");

      fn();
    },
    options,
  );
}
